import React, { useState, useEffect } from 'react';
import {
  FiDownload, FiCalendar, FiBarChart2, FiPieChart, FiTrendingUp, FiDollarSign, FiStar
} from 'react-icons/fi';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import DashboardLayout from '../../components/DashboardLayout';
import API from '../../utils/api';

const StaffDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState('bookings');
  const [dateRange, setDateRange] = useState('month');
  const [bookingsData, setBookingsData] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [servicesData, setServicesData] = useState([]);
  const [mechanicsData, setMechanicsData] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, dateRange]);

  const fetchReportData = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await API.get(`/api/admin/reports/${reportType}`, {
        params: { range: dateRange }
      });

      switch (reportType) {
        case 'bookings':
          setBookingsData(response.data);
          break;
        case 'revenue':
          setRevenueData(response.data);
          break;
        case 'services':
          setServicesData(response.data);
          break;
        case 'mechanics':
          setMechanicsData(response.data);
          break;
        default:
          break;
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Data laporan tidak dapat dimuat saat ini.');
      setBookingsData([]);
      setRevenueData([]);
      setServicesData([]);
      setMechanicsData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReportTypeChange = (type) => {
    setReportType(type);
  };

  const handleDateRangeChange = (e) => {
    setDateRange(e.target.value);
  };

  const downloadReport = () => {
    // Implementasi download report sesuai kebutuhan
    alert('Fitur download laporan akan segera tersedia');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const renderBookingsChart = () => {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-lg font-semibold text-secondary mb-4">Laporan Booking</h2>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={bookingsData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value) => [`${value} booking`, '']} />
              <Legend />
              <Bar dataKey="completed" name="Selesai" fill="#10B981" />
              <Bar dataKey="pending" name="Menunggu" fill="#F59E0B" />
              <Bar dataKey="canceled" name="Dibatalkan" fill="#EF4444" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderRevenueChart = () => {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-lg font-semibold text-secondary mb-4">Laporan Pendapatan</h2>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={revenueData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis tickFormatter={(value) => `${value / 1000000}jt`} />
              <Tooltip formatter={(value) => [formatCurrency(value), 'Pendapatan']} />
              <Legend />
              <Line type="monotone" dataKey="revenue" name="Pendapatan" stroke="#FF6B00" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const COLORS = ['#FF6B00', '#FF8A33', '#FFA866', '#FFC699', '#FFE4CC', '#1A1A1A'];

  const renderServicesChart = () => {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-lg font-semibold text-secondary mb-4">Layanan Terpopuler</h2>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={servicesData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {servicesData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`${value} booking`, '']} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderMechanicsChart = () => {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-lg font-semibold text-secondary mb-4">Performa Montir</h2>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={mechanicsData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              layout="vertical"
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={80} />
              <Tooltip formatter={(value, name) => [name === 'rating' ? `${value}/5` : `${value} job`, name === 'rating' ? 'Rating' : 'Job Selesai']} />
              <Legend />
              <Bar dataKey="completed" name="Job Selesai" fill="#3B82F6" />
              <Bar dataKey="rating" name="Rating" fill="#FF6B00" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderReportContent = () => {
    if (error) {
      return (
        <div className="rounded-lg border border-orange-200 bg-orange-50 p-6 text-sm text-orange-700">
          {error}
        </div>
      );
    }

    switch (reportType) {
      case 'bookings':
        return renderBookingsChart();
      case 'revenue':
        return renderRevenueChart();
      case 'services':
        return renderServicesChart();
      case 'mechanics':
        return renderMechanicsChart();
      default:
        return null;
    }
  };

  return (
    <DashboardLayout userRole="staff" userName="Staff">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-secondary">Dashboard</h1>
          <p className="text-gray-600">Pantau kinerja dengan data dan statistik</p>
        </div>
        <button
          onClick={downloadReport}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors flex items-center"
        >
          <FiDownload className="mr-2" /> Unduh Laporan
        </button>
      </div>

      {/* Filter Controls */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div className="flex space-x-2 mb-4 md:mb-0">
            <button
              onClick={() => handleReportTypeChange('bookings')}
              className={`px-4 py-2 rounded-lg flex items-center ${reportType === 'bookings'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              <FiCalendar className="mr-2" /> Booking
            </button>
            <button
              onClick={() => handleReportTypeChange('revenue')}
              className={`px-4 py-2 rounded-lg flex items-center ${reportType === 'revenue'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              <FiDollarSign className="mr-2" /> Pendapatan
            </button>
            <button
              onClick={() => handleReportTypeChange('services')}
              className={`px-4 py-2 rounded-lg flex items-center ${reportType === 'services'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              <FiPieChart className="mr-2" /> Layanan
            </button>
            <button
              onClick={() => handleReportTypeChange('mechanics')}
              className={`px-4 py-2 rounded-lg flex items-center ${reportType === 'mechanics'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              <FiBarChart2 className="mr-2" /> Montir
            </button>
          </div>

          <div className="flex items-center">
            <span className="mr-2 text-gray-500">Periode:</span>
            <select
              value={dateRange}
              onChange={handleDateRangeChange}
              className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="week">Minggu Ini</option>
              <option value="month">Bulan Ini</option>
              <option value="quarter">3 Bulan</option>
              <option value="year">1 Tahun</option>
            </select>
          </div>
        </div>
      </div>

      {/* Report Content */}
      {loading ? (
        <div className="flex justify-center items-center h-80 bg-white rounded-lg shadow-md">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : (
        renderReportContent()
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Total Booking</h3>
          <div className="flex justify-between items-center">
            <p className="text-2xl font-bold text-secondary">1,248</p>
            <div className="p-2 bg-green-100 rounded-full">
              <FiTrendingUp className="text-green-600" size={20} />
            </div>
          </div>
          <p className="text-sm text-green-600 mt-2">+12.5% dari bulan lalu</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Pendapatan</h3>
          <div className="flex justify-between items-center">
            <p className="text-2xl font-bold text-secondary">Rp 425,5 jt</p>
            <div className="p-2 bg-green-100 rounded-full">
              <FiTrendingUp className="text-green-600" size={20} />
            </div>
          </div>
          <p className="text-sm text-green-600 mt-2">+8.3% dari bulan lalu</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Layanan Terpopuler</h3>
          <div className="flex justify-between items-center">
            <p className="text-2xl font-bold text-secondary">Ganti Oli</p>
            <div className="p-2 bg-primary bg-opacity-10 rounded-full">
              <FiPieChart className="text-primary" size={20} />
            </div>
          </div>
          <p className="text-sm text-primary mt-2">120 booking bulan ini</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Montir Terbaik</h3>
          <div className="flex justify-between items-center">
            <p className="text-2xl font-bold text-secondary">Joko</p>
            <div className="p-2 bg-yellow-100 rounded-full">
              <FiStar className="text-yellow-500" size={20} />
            </div>
          </div>
          <p className="text-sm text-yellow-500 mt-2">Rating 4.9/5 (90 job)</p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default StaffDashboard; 
